import {
	IconActivity,
	IconCash,
	IconDeviceSim,
	IconTrendingUp,
} from "@tabler/icons-react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { AppShell } from "@/components/layout/app-shell"
import { ProtectedRoute } from "@/components/layout/protected-route"
import { Button } from "@/components/ui/button"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card"
import { useMerchantStats } from "@/hooks/use-merchant-stats"

export const Route = createFileRoute("/")({
	component: Dashboard,
})

function Dashboard() {
	return (
		<ProtectedRoute>
			<AppShell>
				<DashboardContent />
			</AppShell>
		</ProtectedRoute>
	)
}

function DashboardContent() {
	const { data: stats, isLoading, error } = useMerchantStats()

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
					<p className="text-muted-foreground">Loading dashboard...</p>
				</div>
			</div>
		)
	}

	if (error) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<p className="text-destructive font-medium">Failed to load dashboard</p>
					<p className="text-sm text-muted-foreground mt-2">
						{error instanceof Error ? error.message : "Unknown error occurred"}
					</p>
				</div>
			</div>
		)
	}

	if (!stats) {
		return null
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">Merchant Dashboard</h1>
				<p className="text-muted-foreground">
					Welcome back, {stats.merchant.businessName}
				</p>
			</div>

			{/* Today's Stats */}
			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Today's Orders
						</CardTitle>
						<IconTrendingUp className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{stats.today.total}</div>
						<p className="text-xs text-muted-foreground">
							{stats.today.completed} completed, {stats.today.pending} pending
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Today's Volume
						</CardTitle>
						<IconCash className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							৳{Number(stats.today.volume).toLocaleString()}
						</div>
						<p className="text-xs text-muted-foreground">
							{stats.today.completed} completed
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Success Rate</CardTitle>
						<IconActivity className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{stats.overall.successRate}
						</div>
						<p className="text-xs text-muted-foreground">
							{stats.today.failed} failed orders
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Balance</CardTitle>
						<IconCash className="h-4 w-4 text-purple-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-purple-600">
							৳{Number(stats.merchant.balance).toLocaleString()}
						</div>
						<p className="text-xs text-muted-foreground">Available balance</p>
					</CardContent>
				</Card>
			</div>

			{/* Quick Links */}
			<div className="grid gap-4 md:grid-cols-3">
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconActivity className="h-5 w-5" />
							View Orders
						</CardTitle>
						<CardDescription>
							Monitor all your deposit and payout orders
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button asChild className="w-full">
							<Link to="/orders">
								<IconActivity className="h-4 w-4 mr-2" />
								View Orders
							</Link>
						</Button>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconCash className="h-5 w-5" />
							Analytics
						</CardTitle>
						<CardDescription>
							Detailed cash flow reports and analytics
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button asChild variant="outline" className="w-full">
							<Link to="/analytics">
								<IconCash className="h-4 w-4 mr-2" />
								View Analytics
							</Link>
						</Button>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconDeviceSim className="h-5 w-5" />
							Assigned SIMs
						</CardTitle>
						<CardDescription>
							View SIM cards assigned to your account
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button asChild variant="outline" className="w-full">
							<Link to="/sims">
								<IconDeviceSim className="h-4 w-4 mr-2" />
								View SIMs
							</Link>
						</Button>
					</CardContent>
				</Card>
			</div>
		</div>
	)
}
